import {
  Play, Square, RotateCcw, Unlock, Sliders, ListChecks, FileDown,
  Upload, Trash2, Calendar, CalendarX, Eraser, MessageSquare, Database,
  CalendarCheck, ListTree, Send,
  type LucideIcon,
} from 'lucide-react';

/**
 * Mirror of the API's COMMANDS catalogue (`apps/api/src/routes/chargers/commands.ts`).
 * Source of truth for what the admin UI knows how to dispatch — fields are the
 * surface area of each command's body editor.
 *
 * Adding a command? Add it to BOTH the API catalogue and this list. The UI
 * surfaces `implemented: false` entries as disabled buttons with a NOT_IMPLEMENTED
 * tooltip, so the operator can see what's coming without us shipping a fake button.
 *
 * Field types deliberately small — `number` / `string` / `enum` cover everything
 * Priority 1 needs. Complex shapes (charging profiles, local lists) ship raw
 * JSON in their parent ticket, not here.
 */

export type CommandFieldType = 'string' | 'number' | 'enum';

export interface CommandField {
  key: string;
  label: string;
  type: CommandFieldType;
  required?: boolean;
  options?: readonly string[];
  defaultValue?: string | number;
  placeholder?: string;
  hint?: string;
}

export interface CommandSpec {
  /** Kebab-case key matching the API route. */
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Coloured accent for the button glyph — kept warm/cool/danger. */
  accent: 'cool' | 'warm' | 'danger' | 'neutral';
  /** Tab grouping in the UI. */
  group: 'session' | 'config' | 'lifecycle' | 'profile' | 'misc';
  /** True when the OCPP-side dispatch is wired. False = stub button. */
  implemented: boolean;
  /** Dispatch needs an extra confirm step (Reset, Unlock, Firmware…). */
  destructive?: boolean;
  fields: CommandField[];
}

export const COMMAND_DEFS: CommandSpec[] = [
  // ── Session ──────────────────────────────────────────────────────
  {
    key: 'remote-start',
    label: 'Remote Start Transaction',
    description: 'Start a session on a connector for the supplied idTag.',
    icon: Play,
    accent: 'cool',
    group: 'session',
    implemented: true,
    fields: [
      { key: 'connectorId', label: 'Connector ID', type: 'number', defaultValue: 1 },
      { key: 'idTag', label: 'ID tag', type: 'string', required: true, placeholder: 'RFID or driver tag' },
    ],
  },
  {
    key: 'remote-stop',
    label: 'Remote Stop Transaction',
    description: 'Stop an active session by transaction ID.',
    icon: Square,
    accent: 'warm',
    group: 'session',
    implemented: true,
    fields: [
      { key: 'transactionId', label: 'Transaction ID', type: 'number', required: true },
    ],
  },
  {
    key: 'unlock-connector',
    label: 'Unlock Connector',
    description: 'Force-release the cable lock on a stuck connector.',
    icon: Unlock,
    accent: 'cool',
    group: 'session',
    implemented: true,
    destructive: true,
    fields: [
      { key: 'connectorId', label: 'Connector ID', type: 'number', required: true, defaultValue: 1 },
    ],
  },

  // ── Lifecycle ────────────────────────────────────────────────────
  {
    key: 'reset',
    label: 'Reset',
    description: 'Reboot the chargepoint. Soft = software, Hard = full power cycle.',
    icon: RotateCcw,
    accent: 'danger',
    group: 'lifecycle',
    implemented: true,
    destructive: true,
    fields: [
      {
        key: 'type', label: 'Type', type: 'enum',
        required: true, options: ['Soft', 'Hard'], defaultValue: 'Soft',
      },
    ],
  },
  {
    key: 'change-availability',
    label: 'Change Availability',
    description: 'Take a connector (or the whole CP) Operative or Inoperative.',
    icon: CalendarCheck,
    accent: 'warm',
    group: 'lifecycle',
    implemented: true,
    fields: [
      { key: 'connectorId', label: 'Connector ID', type: 'number', defaultValue: 0,
        hint: '0 = entire chargepoint' },
      { key: 'type', label: 'Type', type: 'enum',
        required: true, options: ['Operative', 'Inoperative'], defaultValue: 'Inoperative' },
    ],
  },

  // ── Config ───────────────────────────────────────────────────────
  {
    key: 'get-configuration',
    label: 'Get Configuration',
    description: 'Read configuration keys (or all of them).',
    icon: Sliders,
    accent: 'neutral',
    group: 'config',
    implemented: true,
    fields: [
      { key: 'key', label: 'Key (single)', type: 'string',
        placeholder: 'Leave blank for all', hint: 'Comma-separated for multiple keys' },
    ],
  },
  {
    key: 'change-configuration',
    label: 'Change Configuration',
    description: 'Set a single configuration key.',
    icon: Sliders,
    accent: 'cool',
    group: 'config',
    implemented: true,
    fields: [
      { key: 'key', label: 'Key', type: 'string', required: true },
      { key: 'value', label: 'Value', type: 'string', required: true },
    ],
  },
  {
    key: 'trigger-message',
    label: 'Trigger Message',
    description: 'Ask the charger to send a status frame (StatusNotification, BootNotification, ...).',
    icon: MessageSquare,
    accent: 'cool',
    group: 'config',
    implemented: true,
    fields: [
      { key: 'requestedMessage', label: 'Requested message', type: 'enum', required: true,
        options: ['BootNotification', 'StatusNotification', 'Heartbeat', 'MeterValues', 'DiagnosticsStatusNotification', 'FirmwareStatusNotification'],
        defaultValue: 'StatusNotification' },
      { key: 'connectorId', label: 'Connector ID', type: 'number', hint: 'Required for StatusNotification' },
    ],
  },

  // ── Profile / charging-profile ───────────────────────────────────
  // Set + Clear are live: the API dispatches both through the same
  // /internal/command relay the residential amperage route already uses
  // in production (buildSetChargingProfilePayload / buildClear...).
  // Get Composite Schedule stays a stub — it is implemented: false in
  // the API catalogue too (501), so relabeling it here would ship a
  // button that always errors.
  {
    key: 'set-charging-profile',
    label: 'Set Charging Profile',
    description:
      'Cap the charger\'s current at a fixed amp limit (a manual, whole-chargepoint '
      + 'profile). On a DLM-managed circuit the cap is transient -- the control '
      + 'loop\'s next recompute replaces it.',
    icon: ListChecks,
    accent: 'cool',
    group: 'profile',
    implemented: true,
    fields: [
      // The API requires `limit` (amps) and builds the fixed-shape profile
      // server-side (connectorId 0, stable DLM profile id).
      { key: 'limit', label: 'Current limit (A)', type: 'number', required: true,
        hint: 'Amps, applied to the whole chargepoint' },
    ],
  },
  {
    key: 'clear-charging-profile',
    label: 'Clear Charging Profile',
    description: 'Remove the applied charging profile and release the cap.',
    icon: Eraser,
    accent: 'warm',
    group: 'profile',
    implemented: true,
    fields: [],
  },
  {
    key: 'get-composite-schedule',
    label: 'Get Composite Schedule',
    description:
      'Ask the charger to compute the effective charging schedule for a '
      + 'connector over a window -- the net result of every profile stacked on '
      + 'it. The reply renders as a readable limit timeline.',
    icon: ListTree,
    accent: 'neutral',
    group: 'profile',
    implemented: true,
    fields: [
      { key: 'connectorId', label: 'Connector', type: 'number', required: true,
        defaultValue: 0, hint: '0 = whole chargepoint' },
      { key: 'duration', label: 'Window (s)', type: 'number', required: true,
        defaultValue: 3600, hint: 'How far ahead to compute, in seconds' },
      { key: 'chargingRateUnit', label: 'Unit', type: 'enum', options: ['A', 'W'] },
    ],
  },

  // ── Misc / advanced ──────────────────────────────────────────────
  // All live (OCPP command completion). A charger that does not support
  // one replies NotImplemented/NotSupported, which the result view
  // renders as an honest "not supported by this charger" state.
  {
    key: 'get-diagnostics',
    label: 'Get Diagnostics',
    description:
      'Ask the charger to upload its diagnostics archive. The upload target is '
      + 'created automatically (a one-hour signed link into the diagnostics '
      + 'bucket); progress arrives as status notifications shown on this page. '
      + 'Note: some charger firmware only supports FTP upload and may reject '
      + 'the link -- the result will say so honestly.',
    icon: FileDown,
    accent: 'neutral',
    group: 'misc',
    implemented: true,
    fields: [
      { key: 'retries', label: 'Retries', type: 'number', hint: 'Optional upload attempts' },
      { key: 'retryInterval', label: 'Retry interval (s)', type: 'number' },
      { key: 'startTime', label: 'Log window start', type: 'string',
        placeholder: '2026-07-01T00:00:00Z', hint: 'Optional ISO time; limits the log window' },
      { key: 'stopTime', label: 'Log window end', type: 'string',
        placeholder: '2026-07-09T00:00:00Z' },
    ],
  },
  {
    key: 'update-firmware',
    label: 'Update Firmware',
    description:
      'Tell the charger to download a firmware image and install it. A wrong '
      + 'image can PERMANENTLY BRICK the hardware -- dispatch requires typing '
      + 'the charger identity to confirm. Progress (Downloading, Installing, '
      + 'Installed, or the failure states) arrives as notifications shown on '
      + 'this page; the charger usually reboots after installing.',
    icon: Upload,
    accent: 'danger',
    group: 'misc',
    implemented: true,
    destructive: true,
    fields: [
      { key: 'location', label: 'Firmware image URL', type: 'string', required: true,
        placeholder: 'https://.../firmware.bin', hint: 'http(s) or ftp URL the CHARGER downloads from' },
      { key: 'retrieveDate', label: 'Retrieve at', type: 'string', required: true,
        placeholder: '2026-07-10T03:00:00Z', hint: 'ISO time; a past time means download now' },
      { key: 'retries', label: 'Retries', type: 'number' },
      { key: 'retryInterval', label: 'Retry interval (s)', type: 'number' },
    ],
  },
  {
    key: 'clear-cache',
    label: 'Clear Cache',
    description: 'Wipe the charger\'s authorization cache. No parameters.',
    icon: Trash2,
    accent: 'warm',
    group: 'misc',
    implemented: true,
    fields: [],
  },
  {
    key: 'send-local-list',
    label: 'Send Local Auth List',
    description:
      'Push the offline authorization list, built automatically from this '
      + 'charger\'s tenant\'s ACTIVE RFID cards (always a full replace; the '
      + 'version number is managed for you). Note: the list is tenant-scoped, '
      + 'which is stricter than online authorization -- in offline mode a card '
      + 'from another tenant that charges fine online would be refused.',
    icon: Database,
    accent: 'neutral',
    group: 'misc',
    implemented: true,
    fields: [],
  },
  {
    key: 'get-local-list-version',
    label: 'Get Local List Version',
    description:
      'Read the local auth list version the charger holds (0 = empty list, '
      + '-1 = local list not supported). Also re-syncs the version stored here.',
    icon: ListChecks,
    accent: 'neutral',
    group: 'misc',
    implemented: true,
    fields: [],
  },
  {
    key: 'reserve-now',
    label: 'Reserve Now',
    description:
      'Reserve a connector for an idTag until the expiry time. The reservation '
      + 'id is allocated automatically; the charger answers Accepted, or an '
      + 'honest Occupied / Faulted / Rejected / Unavailable. Many AC chargers '
      + 'do not support reservations at all.',
    icon: Calendar,
    accent: 'cool',
    group: 'misc',
    implemented: true,
    fields: [
      { key: 'idTag', label: 'idTag', type: 'string', required: true,
        hint: 'The RFID tag or driver token the reservation admits (max 20 chars)' },
      { key: 'expiryDate', label: 'Hold until', type: 'string', required: true,
        placeholder: '2026-07-09T18:00:00Z', hint: 'ISO time, future, within 7 days' },
      { key: 'connectorId', label: 'Connector', type: 'number',
        hint: 'Blank or 0 = let the charger pick' },
    ],
  },
  {
    key: 'cancel-reservation',
    label: 'Cancel Reservation',
    description: 'Release a reservation by its id (see the reservation list below the form).',
    icon: CalendarX,
    accent: 'warm',
    group: 'misc',
    implemented: true,
    fields: [
      { key: 'reservationId', label: 'Reservation id', type: 'number', required: true,
        hint: 'The id Reserve Now allocated' },
    ],
  },
  {
    key: 'data-transfer',
    label: 'Data Transfer',
    description:
      'Vendor-specific message exchange. Requires the vendor\'s documented '
      + 'vendorId; unknown ids come back Rejected or UnknownVendorId.',
    icon: Send,
    accent: 'neutral',
    group: 'misc',
    implemented: true,
    fields: [
      { key: 'vendorId', label: 'Vendor id', type: 'string', required: true,
        placeholder: 'com.vendor.feature' },
      { key: 'messageId', label: 'Message id', type: 'string' },
      { key: 'data', label: 'Data', type: 'string', hint: 'Free-form string or JSON per the vendor spec' },
    ],
  },
];

export const COMMAND_GROUPS: { key: CommandSpec['group']; label: string }[] = [
  { key: 'session', label: 'Session control' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'config', label: 'Configuration' },
  { key: 'profile', label: 'Charging profiles' },
  { key: 'misc', label: 'Advanced' },
];
